import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { CalendarIcon, Plus, Trash2 } from "lucide-react";
import {
  useCreateStockOut,
  useListInventoryItems,
  useListInventoryCategories,
  useListItemVariants,
  getListStockOutQueryKey,
  getListItemVariantsQueryKey,
} from "@workspace/api-client-react";
import type { InventoryItem, InventoryItemVariant } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { ContactCombobox } from "./contact-combobox";
import { ItemPicker } from "./item-picker";

// Pay-Per-Use stock-out form (F&B, decor, add-ons charged on consumption).
//
// Differs from the With-Limit form (member-stock-out-form.tsx) in three ways:
//   1. Multiple line items per submission (operator logs a whole order, not
//      one item at a time). On Save we loop createStockOut once per line.
//   2. Consumer is picked via ContactCombobox — shows every contact across
//      every account with the account legalName + phone joined. The user
//      explicitly asked for ALL contacts under each account to be visible
//      (not just the primary), 2026-06-19.
//   3. Item rows render the full distinguishing label (brand + name + specs
//      + UOM) so two "Chips" rows with different brands/sizes are tellable
//      apart. Amount per line auto-derives from item.sellingPrice × qty
//      and is shown read-only beside the quantity input.

const PAY_PER_USE_CHARGING_MODELS = ["Pay_Per_Use", "AddOn_Optional"];

const LineSchema = z.object({
  itemId: z.coerce.number().int().positive("Pick an item"),
  variantId: z.coerce.number().int().positive().nullable().optional(),
  quantity: z.coerce.number().positive("Quantity must be > 0"),
});

const PayPerUseSchema = z.object({
  purchaseDate: z.date({ required_error: "Pick a date" }),
  memberId: z.number({ required_error: "Pick a member" }).int().positive("Pick a member"),
  // The specific contact (person) on the account who consumed the
  // item. Submitted alongside memberId so the stock_out row is
  // tagged at the contact level, not just the billing account.
  contactId: z.number().int().positive().nullable().optional(),
  lines: z.array(LineSchema).min(1, "Add at least one item"),
});

type FormValues = z.infer<typeof PayPerUseSchema>;

interface Props {
  onSaved?: () => void;
  onDiscard?: () => void;
}

function lineAmount(it: InventoryItem | undefined, qty: number): number {
  if (!it) return 0;
  const price = Number(
    (it as { sellingPrice?: string | number | null }).sellingPrice ?? 0,
  );
  if (!Number.isFinite(price) || price <= 0) return 0;
  return Math.round(price * qty * 100) / 100;
}

export function PayPerUseForm({ onSaved, onDiscard }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: items = [] } = useListInventoryItems({ status: "Active" });
  const { data: categories = [] } = useListInventoryCategories();
  const createMutation = useCreateStockOut();
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const eligibleItems = useMemo(() => {
    const categoryIdsByModel = new Set(
      categories
        .filter((c) => PAY_PER_USE_CHARGING_MODELS.includes(c.chargingModel))
        .map((c) => c.id),
    );
    return items
      .filter((it) => categoryIdsByModel.has(it.categoryId))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items, categories]);

  const form = useForm<FormValues>({
    resolver: zodResolver(PayPerUseSchema),
    defaultValues: {
      purchaseDate: new Date(),
      memberId: undefined as unknown as number,
      contactId: null,
      lines: [{ itemId: undefined as unknown as number, variantId: null, quantity: 1 }],
    },
  });

  const linesArray = useFieldArray({ control: form.control, name: "lines" });
  const watchedLines = form.watch("lines");

  const lineTotals = (watchedLines ?? []).map((l) =>
    lineAmount(items.find((it) => it.id === l?.itemId), Number(l?.quantity ?? 0)),
  );
  const grandTotal = lineTotals.reduce((sum, n) => sum + n, 0);

  async function onSubmit(values: FormValues) {
    try {
      const transactionDateIso = values.purchaseDate.toISOString();
      // One cart_id stamped on every line of this submission so the
      // Transactions register can group sibling rows and the cart-paid
      // endpoint can flip them all with one UPI ref + proof.
      const cartId =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `cart-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      // Sequential loop so any 422 (insufficient stock) shows the offending
      // line. Parallel Promise.all would race and toast multiples.
      for (const [i, line] of values.lines.entries()) {
        const it = items.find((x) => x.id === line.itemId);
        const amount = lineAmount(it, line.quantity);
        try {
          await createMutation.mutateAsync({
            data: {
              transactionDate: transactionDateIso,
              consumptionType: "Member_Paid_Use",
              itemId: line.itemId,
              quantityIssued: line.quantity,
              memberId: values.memberId,
              accountId: values.memberId,
              contactId: values.contactId ?? null,
              cabinNumber: null,
              amountCharged: amount > 0 ? amount : null,
              variantId: line.variantId ?? null,
              appliedModifiers: null,
              cartId,
            },
          });
        } catch (err: unknown) {
          const anyErr = err as { status?: number; data?: unknown };
          const data = anyErr.data as {
            error?: string;
            details?: { componentName?: string; currentStock?: number; required?: number };
            variantDisplayName?: string;
            variantCurrentStock?: number;
          } | null;
          const which = `Line ${i + 1} (${it?.name ?? `#${line.itemId}`})`;
          if (anyErr.status === 422) {
            if (data?.details?.componentName != null) {
              toast({
                title: `${which}: need ${data.details.required} of ${data.details.componentName} but only ${data.details.currentStock} available.`,
                variant: "destructive",
              });
            } else if (data?.variantDisplayName != null) {
              toast({
                title: `${which}: variant ${data.variantDisplayName} has only ${data.variantCurrentStock} left.`,
                variant: "destructive",
              });
            } else {
              toast({
                title: `${which}: ${data?.error ?? "could not save"}`,
                variant: "destructive",
              });
            }
          } else {
            toast({ title: `${which} failed to save.`, variant: "destructive" });
          }
          return;
        }
      }
      toast({
        title:
          values.lines.length === 1
            ? "Pay-Per-Use charge recorded."
            : `${values.lines.length} Pay-Per-Use charges recorded.`,
      });
      queryClient.invalidateQueries({ queryKey: getListStockOutQueryKey() });
      onSaved?.();
    } catch {
      toast({ title: "Could not save. Please try again.", variant: "destructive" });
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 px-4 pt-2 pb-6 max-w-lg">
        <div>
          <h2 className="text-lg font-semibold">Log Pay-Per-Use Charge</h2>
          <p className="text-xs text-muted-foreground">
            F&amp;B, decor, add-ons — items charged on consumption. Add as many
            line items as the consumer took in this trip; each line is recorded
            as its own STK-OUT entry with the line amount auto-derived from the
            item rate.
          </p>
        </div>

        <FormField
          name="purchaseDate"
          control={form.control}
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>
                Purchase Date <span className="text-destructive">*</span>
              </FormLabel>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {field.value ? format(field.value, "PPP") : "Pick a date"}
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={(d) => {
                      if (d) {
                        field.onChange(d);
                        setDatePickerOpen(false);
                      }
                    }}
                    disabled={(d) => d > new Date()}
                    className="p-4 [--cell-size:2.5rem]"
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2 rounded-md border p-3">
          <p className="text-xs text-muted-foreground">
            Identify the consumer — pick a member.
          </p>
          <FormField
            name="memberId"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Member <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <ContactCombobox
                    value={field.value ?? null}
                    onChange={(accountId, contact) => {
                      field.onChange(accountId);
                      // Tag the specific contact (person) on the stock_out
                      // row, not just the account, so the F&B section can
                      // attribute the charge to who actually consumed it.
                      form.setValue("contactId", contact.id);
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="space-y-3">
          {/* Section heading — plain <p>, NOT <FormLabel>: shadcn's
              FormLabel calls useFormField() and throws (→ white screen)
              outside a <FormField> ancestor. */}
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">
              Line items <span className="text-destructive">*</span>
            </p>
            {eligibleItems.length === 0 && (
              <span className="text-xs text-muted-foreground">
                No items in Pay-Per-Use or Add-On categories.
              </span>
            )}
          </div>

          {linesArray.fields.map((row, idx) => (
            <LineItemRow
              key={row.id}
              index={idx}
              eligibleItems={eligibleItems}
              control={form.control}
              setValue={form.setValue}
              watchedQuantity={Number(watchedLines?.[idx]?.quantity ?? 0)}
              watchedItemId={watchedLines?.[idx]?.itemId ?? 0}
              amount={lineTotals[idx] ?? 0}
              onRemove={
                linesArray.fields.length > 1 ? () => linesArray.remove(idx) : undefined
              }
            />
          ))}

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              linesArray.append({
                itemId: undefined as unknown as number,
                variantId: null,
                quantity: 1,
              })
            }
          >
            <Plus className="mr-2 h-4 w-4" />
            Add another item
          </Button>

          {grandTotal > 0 && (
            <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2">
              <span className="text-sm font-medium">Total (auto-calculated)</span>
              <span className="text-base font-semibold tabular-nums">
                ₹{grandTotal.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onDiscard}>
            Discard
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </form>
    </Form>
  );
}

// Each line: item picker (rich label with brand+specs+UOM), optional
// variant picker (if item has variants), quantity input, and a
// read-only amount badge derived from item rate × qty. The amount is
// the source of truth — operators see exactly what will be billed.
interface LineItemRowProps {
  index: number;
  eligibleItems: InventoryItem[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: any;
  watchedQuantity: number;
  watchedItemId: number;
  amount: number;
  onRemove?: () => void;
}

function LineItemRow({
  index,
  eligibleItems,
  control,
  setValue,
  watchedQuantity,
  watchedItemId,
  amount,
  onRemove,
}: LineItemRowProps) {
  const { data: variants = [] } = useListItemVariants(
    watchedItemId || 0,
    undefined,
    {
      query: {
        queryKey: getListItemVariantsQueryKey(watchedItemId || 0),
        enabled: watchedItemId > 0,
      },
    },
  );
  const hasVariants = variants.length > 0;

  // When the item changes, clear the variantId so a stale selection
  // from the previous item doesn't leak into the new pick.
  useEffect(() => {
    setValue(`lines.${index}.variantId`, null);
  }, [watchedItemId, index, setValue]);

  return (
    <div className="rounded-md border p-3 space-y-3 bg-card">
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs text-muted-foreground">Line {index + 1}</span>
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 -mt-1 -mr-1"
            onClick={onRemove}
            aria-label={`Remove line ${index + 1}`}
          >
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        )}
      </div>

      <FormField
        name={`lines.${index}.itemId`}
        control={control}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Item</FormLabel>
            <FormControl>
              <ItemPicker
                items={eligibleItems}
                value={field.value ?? null}
                onChange={(id) => field.onChange(id)}
                placeholder={
                  eligibleItems.length === 0 ? "No items available" : "Pick an item"
                }
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {hasVariants && (
        <FormField
          name={`lines.${index}.variantId`}
          control={control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Variant <span className="text-destructive">*</span>
              </FormLabel>
              <Select
                value={field.value ? String(field.value) : ""}
                onValueChange={(v) => field.onChange(v ? Number(v) : null)}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a variant…" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {variants.map((v: InventoryItemVariant) => (
                    <SelectItem key={v.id} value={String(v.id)}>
                      {v.displayName ?? `Variant #${v.id}`}{" "}
                      <span className="text-xs text-muted-foreground">
                        (stock: {v.currentStock})
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
        <FormField
          name={`lines.${index}.quantity`}
          control={control}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Quantity</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={field.value ?? ""}
                  onChange={(e) =>
                    field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">Amount</p>
          <div
            className="h-9 rounded-md border bg-muted/30 px-3 flex items-center font-mono text-sm tabular-nums"
            aria-label={`Auto-calculated amount for line ${index + 1}`}
          >
            ₹{(amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      {watchedItemId > 0 && amount === 0 && watchedQuantity > 0 && (
        <p className="text-xs text-amber-700">
          This item has no selling price set — amount will save as null.
        </p>
      )}
    </div>
  );
}
